import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  CheckCircle, 
  FileText, 
  PenTool, 
  Upload, 
  Building2, 
  Calendar,
  Users,
  DollarSign,
  Send,
  Loader2
} from "lucide-react";
import { PurchaseOrderData } from "@/components/campaign/types/purchaseOrderV2";
import { formatAmount } from "@/components/campaign/utils/purchaseOrderUtils";
import { format } from "date-fns";

interface SubmitStepProps {
  purchaseOrderData: PurchaseOrderData;
  onSubmit: () => void;
  isSubmitting: boolean;
}

export const SubmitStep = ({ 
  purchaseOrderData, 
  onSubmit, 
  isSubmitting 
}: SubmitStepProps) => {
  const { 
    poNumber, 
    companyDetails, 
    devices, 
    duration, 
    amount,
    signature,
    stamp,
    companyRegistration,
    vatRegistration,
    termsAccepted,
    selectedLocations,
    budgetCalculations
  } = purchaseOrderData;

  return (
    <div className="space-y-6">
      {/* Submission Summary */}
      <Card className="bg-gradient-to-r from-green-50 to-blue-50 border-green-200">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 text-green-800">
            <CheckCircle className="h-6 w-6" />
            <span>Ready to Submit</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-green-700">
            All required information has been provided. Review the summary below and submit your purchase order.
          </p>
        </CardContent>
      </Card>

      {/* Order Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="h-5 w-5" />
            <span>Order Summary</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-muted-foreground">PO Number</label>
              <p className="font-mono text-lg">{poNumber}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Company</label>
              <p className="font-medium">{companyDetails.companyName}</p>
            </div>
          </div>

          <Separator />

          {/* Campaign Details */}
          <div className="grid grid-cols-3 gap-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Devices</p>
                <p className="text-xl font-bold">{devices}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Calendar className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Duration</p>
                <p className="text-xl font-bold">{duration} days</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-xl font-bold">SAR {formatAmount(amount)}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Completion Checklist */}
      <Card>
        <CardHeader>
          <CardTitle>Completion Checklist</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-3">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <span className="flex-grow">Company information verified</span>
            <Badge variant="outline" className="bg-green-50 text-green-700">
              Complete
            </Badge>
          </div>

          <div className="flex items-center space-x-3">
            <CheckCircle className={`h-5 w-5 ${signature ? 'text-green-600' : 'text-muted-foreground'}`} />
            <span className="flex-grow">Digital signature provided</span>
            <Badge variant={signature ? "outline" : "secondary"} 
                   className={signature ? "bg-green-50 text-green-700" : ""}>
              {signature ? 'Complete' : 'Pending'}
            </Badge>
          </div>

          <div className="flex items-center space-x-3">
            <CheckCircle className={`h-5 w-5 ${stamp ? 'text-green-600' : 'text-muted-foreground'}`} />
            <span className="flex-grow">Company stamp uploaded</span>
            <Badge variant={stamp ? "outline" : "secondary"} 
                   className={stamp ? "bg-green-50 text-green-700" : ""}>
              {stamp ? 'Complete' : 'Pending'}
            </Badge>
          </div>

          <div className="flex items-center space-x-3">
            <CheckCircle className={`h-5 w-5 ${companyRegistration ? 'text-green-600' : 'text-muted-foreground'}`} />
            <span className="flex-grow">Company registration document</span>
            <Badge variant={companyRegistration ? "outline" : "secondary"} 
                   className={companyRegistration ? "bg-green-50 text-green-700" : ""}>
              {companyRegistration ? 'Complete' : 'Pending'}
            </Badge>
          </div>

          <div className="flex items-center space-x-3">
            <CheckCircle className={`h-5 w-5 ${vatRegistration ? 'text-green-600' : 'text-muted-foreground'}`} />
            <span className="flex-grow">VAT registration document</span>
            <Badge variant={vatRegistration ? "outline" : "secondary"} 
                   className={vatRegistration ? "bg-green-50 text-green-700" : ""}>
              {vatRegistration ? 'Complete' : 'Optional'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Final Actions */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Send className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-900">Submit Your Purchase Order</h3>
              <p className="text-sm text-blue-700 mt-1">
                Once submitted, you'll receive a confirmation email with your purchase order document. 
                Our team might contact you within 1 business day to confirm the campaign details.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* What Happens Next */}
      <Card>
        <CardHeader>
          <CardTitle>What Happens Next?</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-blue-600 text-xs font-bold">1</span>
              </div>
              <div>
                <p className="font-medium">Immediate Confirmation</p>
                <p className="text-muted-foreground">You'll receive an email confirmation with your purchase order PDF</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-blue-600 text-xs font-bold">2</span>
              </div>
              <div>
                <p className="font-medium">Review & Approval</p>
                <p className="text-muted-foreground">Our team might contact you within 1 business day</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-blue-600 text-xs font-bold">3</span>
              </div>
              <div>
                <p className="font-medium">Campaign Launch</p>
                <p className="text-muted-foreground">Once approved and payment is confirmed, your campaign will launch as scheduled</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};